/**
 * Dossha Beden Bulucu — arayüz katmanı
 *
 * Hesabın tamamı size-finder-engine.js içindedir; bu dosya yalnızca soruları
 * sorar ve sonucu sunar.
 *
 * Akış ölçüyle başlar ve ölçü zorunludur: boy/kilodan tahmin yürütüp beden
 * önermiyoruz. Yalnızca o üründe sonucu belirleyen ölçüler sorulur (bir üstte
 * basen sorulmaz), böylece zorunluluk gereksiz sürtünmeye dönüşmez. Ürün
 * sayfasında olduğumuz için kategori sorulmaz, ürün tipinden gelir.
 *
 * Profil localStorage'da saklanır; kullanıcı ölçüsünü bir kez girer,
 * sonraki ürün sayfalarında bedeni doğrudan görür.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'dossha-size-profile';
  const Engine = window.DosshaSizeEngine;

  const FIT_OPTIONS = [
    { id: 'tight',  shift: -1, label: 'Dar olsun, vücudumu sarsın', desc: 'Bir beden küçük öneririz; forma veren, sıkı duruş' },
    { id: 'normal', shift: 0,  label: 'Normal beden',               desc: 'Kalıbın tasarlandığı gibi dursun' },
    { id: 'loose',  shift: 1,  label: 'Bol olsun, rahat dursun',    desc: 'Bir beden büyük öneririz; serbest duruş' },
  ];

  const BRA_BANDS = ['65', '70', '75', '80', '85', '90', '95'];
  const BRA_CUPS = ['A', 'B', 'C', 'D', 'E', 'F'];

  const MEASURE_LABEL = {
    bust:  { name: 'Göğüs çevresi', short: 'göğüs', hint: 'Göğsün en dolgun yerinden, mezurayı yere paralel tutarak ölç.' },
    waist: { name: 'Bel çevresi',   short: 'bel',   hint: 'Belin en ince yerinden, nefesini tutmadan ölç.' },
    hip:   { name: 'Basen çevresi', short: 'basen', hint: 'Kalçanın en geniş yerinden ölç.' },
  };

  // Düşük güvende de iki beden gösterildiği için etiket "emin değiliz" demez;
  // kullanıcıyı korkutmadan durumu bildirir. Asıl ayrım, sonuca gelmeden önce
  // ek ölçü sorulmuş olmasıdır.
  const LEVEL_TEXT = {
    high:   { label: 'Yüksek güven', tone: 'high' },
    medium: { label: 'İki beden arasındasın', tone: 'medium' },
    low:    { label: 'İki beden arasındasın', tone: 'low' },
    out_of_range: { label: 'Beden aralığımızın dışında', tone: 'warn' },
  };

  /** TR bedenleri çift sayıdır; "TR 39" diye bir beden yok. */
  function nearestTrSize(value) {
    return Math.round(value / 2) * 2;
  }

  /** "göğüs, bel ve basen" */
  function listTr(items) {
    if (items.length <= 1) return items[0] || '';
    return items.slice(0, -1).join(', ') + ' ve ' + items[items.length - 1];
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function loadProfile() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Hangi ölçünün gerekli olduğu ürüne göre değiştiği için burada yalnızca
      // biçim doğrulanır; yeterlilik kontrolü SizeFinder.isReady'de yapılır.
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function saveProfile(profile) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch (e) {
      /* özel sekmede localStorage kapalı olabilir; akış yine de çalışır */
    }
  }

  class SizeFinder {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.stepsEl = root.querySelector('[data-sf-steps]');
      this.footerEl = root.querySelector('[data-sf-footer]');
      this.backBtn = root.querySelector('[data-sf-back]');
      this.nextBtn = root.querySelector('[data-sf-next]');

      this.answers = Object.assign({ fit: 'normal', fitShift: 0 }, loadProfile() || {});

      this.steps = [];
      this.index = 0;

      this.backBtn.addEventListener('click', () => this.prev());
      this.nextBtn.addEventListener('click', () => this.next());
    }

    get isBra() {
      return this.config.category === 'bra';
    }

    /**
     * Bu üründe sonucu belirleyen ölçüler; yalnızca bunlar zorunlu tutulur.
     * Config yer değiştirebildiği (ör. tek sayfada ürün geçişi) için
     * constructor'da dondurulmaz, config değişince yeniden hesaplanır.
     */
    get requiredMeasures() {
      if (this._requiredFor !== this.config) {
        const req = Engine.requiredMeasures({ chart: this.config.chart, category: this.config.category });
        this._required = req.length ? req : ['bust'];
        this._requiredFor = this.config;
      }
      return this._required;
    }

    /** Öneri verilebilmesi için gereken her şey elimizde mi? */
    get isReady() {
      if (this.isBra) return !!(this.answers.braBand && this.answers.braCup);
      return this.requiredMeasures.every((key) => this.answers[key] != null);
    }

    buildSteps() {
      this.steps = [this.isBra ? 'bra' : 'measure', 'fit', 'result'];
    }

    /** Ölçüler kayıtlıysa soruları atlayıp doğrudan sonucu göster. */
    open(options) {
      const opts = options || {};
      this.buildSteps();

      this.root.hidden = false;
      if (this.guideView()) this.guideView().hidden = true;

      this.index = this.isReady && !opts.forceRestart ? this.steps.indexOf('result') : 0;
      this.render();
    }

    close() {
      this.root.hidden = true;
      if (this.guideView()) this.guideView().hidden = false;
    }

    guideView() {
      return this.root.closest('.size-guide-drawer__inner')?.querySelector('[data-guide-view]');
    }

    restart() {
      this.buildSteps();
      this.index = 0;
      this.render();
    }

    clearProfile() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* yok sayılır */ }
      this.answers = { fit: 'normal', fitShift: 0 };
      this.restart();
    }

    next() {
      if (this.index < this.steps.length - 1) {
        this.index++;
        this.render();
      }
    }

    prev() {
      if (this.index > 0) {
        this.index--;
        this.render();
      }
    }

    compute() {
      return Engine.recommend(this.answers, {
        chart: this.config.chart,
        category: this.config.category,
        variants: this.config.variants,
        requireMeasured: true,
      });
    }

    /* ---------------------------------------------------------------- *
     * Görünüm
     * ---------------------------------------------------------------- */

    render() {
      const step = this.steps[this.index];
      this.stepsEl.innerHTML = '';

      const renderer = {
        measure: () => this.renderMeasure(),
        bra: () => this.renderBra(),
        fit: () => this.renderFit(),
        result: () => this.renderResult(),
      }[step];

      this.stepsEl.appendChild(renderer());

      const isResult = step === 'result';
      this.footerEl.hidden = isResult;
      this.backBtn.hidden = this.index === 0;

      if (!isResult) this.updateProgress();
      this.validate();
    }

    updateProgress() {
      const total = this.steps.filter((s) => s !== 'result').length;
      const bar = this.root.querySelector('[data-sf-progress]');
      if (bar) {
        bar.hidden = false;
        bar.style.setProperty('--sf-progress', `${((this.index + 1) / total) * 100}%`);
        bar.setAttribute('aria-valuenow', String(this.index + 1));
        bar.setAttribute('aria-valuemax', String(total));
      }
    }

    validate() {
      const step = this.steps[this.index];
      let valid = true;
      if (step === 'measure' || step === 'bra') valid = this.isReady;
      else if (step === 'fit') valid = !!this.answers.fit;
      this.nextBtn.disabled = !valid;
      this.nextBtn.textContent = this.steps[this.index + 1] === 'result' ? 'Bedenimi göster' : 'Devam et';
    }

    stepHeader(parent, title, desc) {
      parent.appendChild(el('h3', 'size-finder__step-title', title));
      if (desc) parent.appendChild(el('p', 'size-finder__step-desc', desc));
    }

    renderMeasure() {
      const wrap = el('div', 'size-finder__step');
      const names = this.requiredMeasures.map((key) => MEASURE_LABEL[key].short);

      this.stepHeader(
        wrap,
        'Ölçülerini gir',
        `Boy ve kilodan tahmin yürütmüyoruz; yanlış beden çıkmasının en yaygın sebebi bu. Bu üründe ${listTr(names)} ölçün belirleyici, mezurayla ölçüp gir.`
      );

      const fields = el('div', 'size-finder__fields');
      this.requiredMeasures.forEach((key) => {
        fields.appendChild(this.numberField({
          key,
          label: MEASURE_LABEL[key].name,
          unit: 'cm',
          min: 50, max: 160, step: 0.5,
          hint: MEASURE_LABEL[key].hint,
        }));
      });
      wrap.appendChild(fields);

      wrap.appendChild(el(
        'p',
        'size-finder__field-hint size-finder__measure-help',
        'Mezuran yoksa bir ipi vücuduna sarıp işaretle, sonra cetvelle ölç. İç çamaşırının üzerinden, mezurayı ne gerdirerek ne de sarkıtarak ölçmen yeterli.'
      ));

      return wrap;
    }

    numberField(spec) {
      const field = el('div', 'size-finder__field');
      const id = `sf-${spec.key}`;
      const label = el('label', 'size-finder__label', spec.label);
      label.htmlFor = id;
      field.appendChild(label);

      const row = el('div', 'size-finder__input-row');
      const input = el('input', 'size-finder__input');
      input.type = 'number';
      input.id = id;
      input.inputMode = 'decimal';
      input.min = spec.min;
      input.max = spec.max;
      input.step = spec.step;
      if (this.answers[spec.key]) input.value = this.answers[spec.key];
      input.addEventListener('input', () => {
        // Aralık dışı ya da boş değer null olarak saklanır; böylece "Devam et"
        // kapalı kalır ve ekranda görünenle hesaba giren değer ayrışmaz.
        const value = Number(input.value);
        const valid = input.value !== '' && !Number.isNaN(value) && value >= spec.min && value <= spec.max;
        this.answers[spec.key] = valid ? value : null;
        this.validate();
      });
      row.appendChild(input);
      row.appendChild(el('span', 'size-finder__input-unit', spec.unit));
      field.appendChild(row);

      if (spec.hint) field.appendChild(el('p', 'size-finder__field-hint', spec.hint));
      return field;
    }

    renderBra() {
      const wrap = el('div', 'size-finder__step');
      this.stepHeader(
        wrap,
        'Sütyen bedenin',
        'Sporcu sütyeninde göğüs altı ölçüsü ve kupa, bedeni belirleyen asıl etken. Tahmin yürütmemek için ikisini de soruyoruz.'
      );

      const fields = el('div', 'size-finder__fields');

      const bandField = el('div', 'size-finder__field');
      bandField.appendChild(el('span', 'size-finder__label', 'Göğüs altı (bant)'));
      bandField.appendChild(this.chipRow(BRA_BANDS, 'braBand'));
      fields.appendChild(bandField);

      const cupField = el('div', 'size-finder__field');
      cupField.appendChild(el('span', 'size-finder__label', 'Kupa'));
      cupField.appendChild(this.chipRow(BRA_CUPS, 'braCup'));
      fields.appendChild(cupField);

      wrap.appendChild(fields);

      wrap.appendChild(el(
        'p',
        'size-finder__field-hint',
        'Bedenini bilmiyorsan: göğüs altını sıkıca ölç, en yakın bant değerini seç. Kupa, göğüs çevresi ile göğüs altı arasındaki farktan gelir — 13 cm A, 15 cm B, 17 cm C, 19 cm D.'
      ));
      return wrap;
    }

    chipRow(values, key) {
      const row = el('div', 'size-finder__chips');
      values.forEach((value) => {
        const chip = el('button', 'size-finder__chip', value);
        chip.type = 'button';
        if (this.answers[key] === value) chip.classList.add('is-selected');
        chip.addEventListener('click', () => {
          row.querySelectorAll('.size-finder__chip').forEach((c) => c.classList.remove('is-selected'));
          chip.classList.add('is-selected');
          this.answers[key] = value;
          this.validate();
        });
        row.appendChild(chip);
      });
      return row;
    }

    renderFit() {
      const wrap = el('div', 'size-finder__step');
      this.stepHeader(wrap, 'Nasıl dursun?', 'Bu ürünün tasarlandığı duruşa göre ayarlıyoruz.');

      const list = el('div', 'size-finder__cards size-finder__cards--stack');
      FIT_OPTIONS.forEach((option) => {
        const btn = el('button', 'size-finder__card');
        btn.type = 'button';
        btn.appendChild(el('span', 'size-finder__card-label', option.label));
        btn.appendChild(el('span', 'size-finder__card-desc', option.desc));
        if (this.answers.fit === option.id) btn.classList.add('is-selected');
        btn.addEventListener('click', () => {
          list.querySelectorAll('.size-finder__card').forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          this.answers.fit = option.id;
          this.answers.fitShift = option.shift;
          this.validate();
        });
        list.appendChild(btn);
      });
      wrap.appendChild(list);
      return wrap;
    }

    /* ---------------------------------------------------------------- *
     * Sonuç
     * ---------------------------------------------------------------- */

    renderResult() {
      const result = this.compute();
      const wrap = el('div', 'size-finder__step size-finder__result');

      if (!result.ok) {
        // Ölçü eksikse bu bir hata değil, tamamlanmamış bir akıştır; kullanıcıyı
        // beden tablosuna göndermek yerine ölçü adımına geri al.
        if (result.reason === 'needs_measurements') {
          wrap.appendChild(el('h3', 'size-finder__step-title', 'Önce ölçülerin gerekiyor'));
          wrap.appendChild(el('p', 'size-finder__step-desc', 'Tahmine dayalı beden önermiyoruz. Ölçülerini girersen kesin sonucu gösterelim.'));
          const back = el('button', 'size-finder__alt-btn', 'Ölçülerimi gireyim');
          back.type = 'button';
          back.addEventListener('click', () => this.restart());
          wrap.appendChild(back);
          return wrap;
        }
        wrap.appendChild(el('h3', 'size-finder__step-title', 'Bu ürün için öneri veremiyoruz'));
        wrap.appendChild(el('p', 'size-finder__step-desc', 'Beden tablosunu inceleyerek seçim yapabilirsin.'));
        wrap.appendChild(this.resultActions());
        return wrap;
      }

      this.lastResult = result;
      saveProfile(this.answers);

      const level = LEVEL_TEXT[result.level] || LEVEL_TEXT.medium;

      // Skala dışıysa beden göstermek yanıltıcı olur; durumu açıkça söyle.
      if (result.outOfRange) {
        wrap.appendChild(el('span', 'size-finder__badge size-finder__badge--warn', level.label));
        wrap.appendChild(el('h3', 'size-finder__result-title',
          result.outOfRange === 'above'
            ? 'Ölçülerin en büyük bedenimizin üzerinde'
            : 'Ölçülerin en küçük bedenimizin altında'));
        wrap.appendChild(el('p', 'size-finder__result-note',
          `Bu üründe sana en yakın beden ${result.primary}, ancak ölçülerin bu ürünün beden aralığının ${result.outOfRange === 'above' ? 'üzerinde' : 'altında'} kalıyor. Rahat bir kullanım için önerimiz, satın almadan önce müşteri hizmetlerimize danışman.`));
        wrap.appendChild(this.stockRow(result, result.primary));
        wrap.appendChild(this.resultActions());
        return wrap;
      }

      wrap.appendChild(el('span', `size-finder__badge size-finder__badge--${level.tone}`, level.label));
      wrap.appendChild(el('p', 'size-finder__result-label', 'Senin bedenin'));
      wrap.appendChild(el('p', 'size-finder__result-size', result.primaryLetter));

      // "M/L" gibi birleşik varyantlarda tek harf gösterip, satın alınacak
      // etiketin neden farklı olduğunu ayrıca söylüyoruz.
      if (result.combinedWith.length) {
        wrap.appendChild(el(
          'p',
          'size-finder__result-variant',
          `${result.primaryLetter} ve ${result.combinedWith.join(', ')} bu üründe tek bedende birleşiyor: ${result.primary}`
        ));
      }

      wrap.appendChild(el('p', 'size-finder__result-note', this.explain(result)));

      // Kararsızlıkta iki beden birlikte gösterilir; hangisinin ne yapacağı yazılır.
      if (result.alternative) {
        wrap.appendChild(this.alternativeBlock(result));
      }

      wrap.appendChild(this.stockRow(result, result.primary));
      wrap.appendChild(this.addToCartButton(result));
      wrap.appendChild(this.summaryBlock(result));
      wrap.appendChild(this.resultActions());
      return wrap;
    }

    /** Öneriyi kullanıcının anlayacağı dille gerekçelendirir. */
    explain(result) {
      const parts = [];
      const tr = nearestTrSize(result.trEstimate);
      // Yalnızca hesaba giren ölçülerin kaynağına bakılır.
      const used = Object.keys(result.perMeasure).map((k) => result.sources[k]);

      if (used.indexOf('bra') > -1) {
        parts.push(`Sütyen bedenin TR ${tr} bedene denk geliyor.`);
      } else {
        parts.push(`Girdiğin ölçüler TR ${tr} bedene denk geliyor.`);
      }

      if (result.fit) {
        const model = {
          compression: 'Bu model yüksek kompresyonlu, vücudu sıkıca sarar.',
          fitted: 'Bu model vücuda oturan bir kesime sahip.',
          regular: 'Bu model normal kalıplı.',
          relaxed: 'Bu model rahat kalıplı.',
          oversized: 'Bu model oversize kalıplı, bilerek bol durur.',
        }[result.fit.key];
        if (model) parts.push(model);
      }

      if (result.fitCapped === 'already_loose') {
        parts.push('Zaten bol durduğu için beden büyütmene gerek yok.');
      } else if (result.fitCapped === 'already_tight') {
        parts.push('Zaten dar kalıplı olduğu için beden küçültmüyoruz.');
      } else if (result.fitShift > 0) {
        parts.push('Rahat duruş tercihine göre bir beden büyüttük.');
      } else if (result.fitShift < 0) {
        parts.push('Vücuda oturan duruş tercihine göre bir beden küçülttük.');
      }

      // Yüksek güvende bu uyarı kendi kendisiyle çelişiyor; yalnızca sonuç
      // gerçekten sınırdayken gösterilir.
      if (result.disagreement >= 2 && result.level !== 'high') {
        parts.push('Üst ve alt ölçülerin farklı bedenlere denk geldiği için tabloyu da kontrol etmeni öneririz.');
      }
      return parts.join(' ');
    }

    alternativeBlock(result) {
      const box = el('div', 'size-finder__alt');
      const alt = result.alternative;
      const altStock = result.stock[alt] || {};

      const trPrimary = result.primaryLetter;
      const altLabel = result.alternativeLetter;
      const goingUp = this.isLarger(alt, result.primary);

      box.appendChild(el('p', 'size-finder__alt-title', `Alternatif: ${altLabel}`));
      let text;
      if (result.sizingHint === 'consider_up') {
        text = `${trPrimary} sana denk geliyor ama bu model kendi grubuna göre dar kalıyor. Daha rahat bir duruş istersen ${altLabel} tercih et.`;
      } else if (result.sizingHint === 'consider_down') {
        text = `${trPrimary} sana denk geliyor ama bu model bilerek bol. Daha toplu bir duruş istersen ${altLabel} tercih et.`;
      } else if (goingUp) {
        text = `İki bedenin sınırındasın. ${trPrimary} daha oturur, ${altLabel} daha rahat durur.`;
      } else {
        text = `İki bedenin sınırındasın. ${trPrimary} daha rahat durur, ${altLabel} daha oturur.`;
      }
      box.appendChild(el('p', 'size-finder__alt-desc', text));

      if (!altStock.available) {
        box.appendChild(el('p', 'size-finder__alt-stock', `${alt} şu an stokta yok.`));
      } else {
        const btn = el('button', 'size-finder__alt-btn', `${alt} bedeni seç`);
        btn.type = 'button';
        btn.addEventListener('click', () => this.selectSize(alt));
        box.appendChild(btn);
      }
      return box;
    }

    isLarger(a, b) {
      const order = (this.config.chart?.headers || []).slice(1);
      return order.indexOf(a) > order.indexOf(b);
    }

    stockRow(result, size) {
      const stock = result.stock[size] || {};
      const row = el('p', 'size-finder__stock');
      if (!stock.found) {
        row.textContent = `${size} bedeni bu üründe bulunmuyor.`;
        row.classList.add('is-out');
      } else if (!stock.available) {
        row.textContent = `${size} bedeni şu an stokta yok.`;
        row.classList.add('is-out');
      } else {
        row.textContent = `${size} bedeni stokta.`;
      }
      return row;
    }

    addToCartButton(result) {
      const stock = result.stock[result.primary] || {};
      const btn = el('button', 'size-finder__cta button button--filled');
      btn.type = 'button';
      if (!stock.available) {
        btn.textContent = `${result.primary} bedeni stokta yok`;
        btn.disabled = true;
      } else {
        btn.textContent = `${result.primary} bedeni seç`;
        btn.addEventListener('click', () => this.selectSize(result.primary));
      }
      return btn;
    }

    summaryBlock(result) {
      const details = el('details', 'size-finder__summary');
      details.appendChild(el('summary', 'size-finder__summary-title', 'Bu sonuç nasıl hesaplandı?'));
      const list = el('dl', 'size-finder__summary-list');

      const originLabel = {
        measured: 'girdiğin ölçü',
        bra: 'sütyen bedeninden',
        estimated: 'tahmin',
      };
      // Yalnızca hesaba giren ölçüler listelenir; ağırlığı sıfır olan bir
      // ölçüyü göstermek, sonucu etkilediği izlenimi verir.
      const rows = [];
      [['bust', 'Göğüs'], ['waist', 'Bel'], ['hip', 'Basen']].forEach(([key, name]) => {
        if (!result.perMeasure[key]) return;
        rows.push([name, `${result.body[key]} cm (${originLabel[result.sources[key]] || 'tahmin'})`]);
      });
      if (result.fit) {
        rows.push(['Ürünün kalıbı', `${result.fit.label} (bolluk payı ${result.fit.ease > 0 ? '+' : ''}${result.fit.ease} cm)`]);
      }
      // Tam isabet iddiası gerçekçi değil; en fazla %99 gösterilir.
      rows.push(['Güven', `%${Math.min(99, Math.round(result.confidence * 100))}`]);

      rows.forEach(([term, value]) => {
        list.appendChild(el('dt', null, term));
        list.appendChild(el('dd', null, value));
      });
      details.appendChild(list);
      return details;
    }

    resultActions() {
      const actions = el('div', 'size-finder__result-actions');
      const edit = el('button', 'size-finder__link', 'Yanıtlarımı değiştir');
      edit.type = 'button';
      edit.addEventListener('click', () => this.restart());
      actions.appendChild(edit);

      const clear = el('button', 'size-finder__link', 'Profilimi sil');
      clear.type = 'button';
      clear.addEventListener('click', () => this.clearProfile());
      actions.appendChild(clear);
      return actions;
    }

    /* ---------------------------------------------------------------- *
     * Ürün formuna bağlanma
     * ---------------------------------------------------------------- */

    /** Beden seçicinin adı ürün seçeneğinden gelir ve temada "Beden-2" gibi
     *  soneklerle üretilir; bu yüzden tam eşitlik yerine ön ek kontrolü. */
    isSizeControl(node) {
      const optionName = (this.config.sizeOptionName || 'Beden').trim().toLocaleLowerCase('tr-TR');
      const name = String(node.name || '').trim().toLocaleLowerCase('tr-TR');
      if (!name) return true; // adı yoksa değerine bakarak deneriz
      return name.indexOf(optionName) === 0;
    }

    /** Bedeni sepete atmak yerine varyant seçicide işaretler; kullanıcı
     *  ürünün geri kalanını (renk, adet) kendi kontrolünde tutar. */
    selectSize(size) {
      const canon = Engine.canonSize(size);
      const scope =
        document.querySelector('product-variant-selects, variant-radios, variant-selects, .product-form') ||
        document;

      let matched = false;

      scope.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (matched || input.disabled) return;
        if (!this.isSizeControl(input)) return;
        if (Engine.canonSize(input.value) !== canon) return;
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        matched = true;
      });

      if (!matched) {
        scope.querySelectorAll('select').forEach((select) => {
          if (matched || !this.isSizeControl(select)) return;
          const option = Array.prototype.find.call(select.options, function (o) {
            return Engine.canonSize(o.value) === canon && !o.disabled;
          });
          if (!option) return;
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          matched = true;
        });
      }

      if (typeof window.closeSizeGuideDrawer === 'function') window.closeSizeGuideDrawer();
      else this.close();

      const submit = document.querySelector('.product-form__submit');
      if (matched && submit) {
        submit.classList.add('size-finder-highlight');
        submit.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => submit.classList.remove('size-finder-highlight'), 2000);
      } else if (!matched) {
        // Sessizce başarısız olmak, kullanıcının yanlış bedenle devam etmesine
        // yol açar; en azından beden seçiciye götür.
        const picker = document.querySelector('product-variant-selects, variant-radios, .product-form');
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        console.warn('Beden bulucu: "' + size + '" varyant seçicide bulunamadı.');
      }
    }
  }

  /** Bulucu bu sayfada kurulamadıysa giriş butonu kullanıcıyı boşa çıkarır.
   *  Beden rehberi çekmecesi yalnızca ürün tipi tema listelerinde eşleşince
   *  render edildiği için bu durum gerçekten oluşabilir. */
  function hideEntryPoints() {
    document.querySelectorAll('[data-size-finder-open]').forEach(function (node) {
      if (node.classList.contains('size-finder-entry')) node.hidden = true;
    });
  }

  function init() {
    const root = document.querySelector('[data-size-finder]');
    const configEl = document.querySelector('[data-size-finder-config]');
    if (!root || !configEl || !Engine) {
      hideEntryPoints();
      return;
    }

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Beden bulucu yapılandırması okunamadı:', e);
      hideEntryPoints();
      return;
    }
    if (!config.chart || !Engine.parseChart(config.chart)) {
      hideEntryPoints();
      return;
    }

    const finder = new SizeFinder(root, config);
    window.dosshaSizeFinder = finder;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-size-finder-open]');
      if (!trigger) return;
      event.preventDefault();
      if (typeof window.openSizeGuideDrawer === 'function') window.openSizeGuideDrawer();
      finder.open();
    });

    // Ölçüler kayıtlıysa tetikleyicinin üstünde bedeni hemen göster.
    const profile = loadProfile();
    if (profile) {
      const preview = Engine.recommend(
        Object.assign({}, profile),
        {
          chart: config.chart,
          category: config.category,
          variants: config.variants,
          requireMeasured: true,
        }
      );
      if (preview.ok && !preview.outOfRange) {
        document.querySelectorAll('[data-size-finder-preview]').forEach((node) => {
          node.textContent = `Senin bedenin: ${preview.primaryLetter}`;
          node.hidden = false;
        });
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
